import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import Alpi from '../alpi/Alpi.jsx';
import Device from '@core/scripts/components/device';
import {Constants} from '../view-constants';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import useEffectOnUpdate from '../helper/hooks-helper';
import {useDispatch} from 'react-redux';
import {loadOff} from '../LoaderSlice.jsx';
import {yieldToMain} from '../ArticleOverviewHelper.js';
import {useBreakpointCurrent} from 'bonprix-design-system';

function AlpiList({
    alpiList,
    shopContent,
    filterTrackingValue,
    pointOfSaleId,
    checkChunks,
    actualPageSize,
    loadChunkRange,
    navigateToProduct,
    updateScrollPosition,
    contentLoadedCounter,
    teaserContent,
    rankingTrackingValue,
}) {
    const currentBreakpoint = useBreakpointCurrent();
    const breakpointM = currentBreakpoint === 'm';

    const {chunkSize, numberOfChunks, currentSearchParams} = useContext(articleOverviewStaticContext);

    const PAGE_SIZE = chunkSize * numberOfChunks;

    const [latestAlpiIndexLoaded, setLatestAlpiIndexLoaded] = useState(0);

    const currentChunk = useRef(1);
    const loading = useRef(false);
    const jumpToPreviousLocation = useRef(true);
    const currentMiddleElement = useRef(undefined);
    const alpiListRef = useRef(null);
    const articleCount = alpiList.filter((alpi) => {
        return alpi.gridItem.displayType === Constants.GRIDITEM_ARTICLE;
    }).length;
    const numberOfEntryItems = useRef(Math.max(0, articleCount - chunkSize));

    const dispatch = useDispatch();

    useEffect(() => {
        if (Device.isApp()) {
            window.addEventListener('scroll', keepScrollPosition, {passive: true});
        }
        const anchorIndex = parseInt(currentSearchParams.current.get(Constants.SEARCH_PARAMS_ANCHOR));
        if (anchorIndex < PAGE_SIZE) {
            const destinationChunk = Math.ceil((anchorIndex + 1) / chunkSize);
            destinationChunk === currentChunk.current
                ? scrollToAnchorAfterLoad(anchorIndex)
                : loadChunkRange(currentChunk.current + 1, destinationChunk, articleCount);
            currentChunk.current = destinationChunk;
        } else {
            jumpToPreviousLocation.current = false;
        }
    }, []);

    useEffectOnUpdate(() => {
        // reset chunk and latestAlpiIndexLoaded an calculate entryItems for new article list (ordering, sorting, filtering)
        currentChunk.current = 1;
        numberOfEntryItems.current = Math.max(0, articleCount - chunkSize * currentChunk.current);
        setLatestAlpiIndexLoaded(0);
    }, [contentLoadedCounter]);

    useEffectOnUpdate(() => {
        if (jumpToPreviousLocation.current) {
            const anchorIndex = parseInt(currentSearchParams.current.get(Constants.SEARCH_PARAMS_ANCHOR));
            if (anchorIndex && articleCount > anchorIndex) {
                scrollToAnchorAfterLoad(anchorIndex);
            }
        }

        loading.current = false;
    }, [articleCount]);

    useEffectOnUpdate(() => {
        handleChunkLoading(latestAlpiIndexLoaded);
    }, [latestAlpiIndexLoaded]);

    useEffect(() => {
        checkChunks(currentChunk.current);
    }, [currentChunk.current]);

    const calculateLatestLoadedAlpi = useCallback(
        (alpiIndex) => {
            const nextAlpiPositionIndex =
                alpiIndex +
                (breakpointM ? Constants.PRELOADED_ALPIS_MEDIUM : Constants.PRELOADED_ALPIS_SMALL_AND_LARGE);
            setLatestAlpiIndexLoaded((prevIndex) => Math.max(prevIndex, nextAlpiPositionIndex));
        },
        [breakpointM],
    );

    return (
        <div className="alpi-list__wrapper" ref={alpiListRef}>
            {renderAlpis()}
        </div>
    );

    function renderAlpis() {
        const firstFillerArticle = alpiList.find((alpi) => {
            return alpi.gridItem.displayType === Constants.GRIDITEM_FILLER_ARTICLE;
        });

        const tempBreakpoint = currentBreakpoint.toUpperCase();

        const maxOrdering = firstFillerArticle ? firstFillerArticle.orderings[tempBreakpoint] : Number.MAX_SAFE_INTEGER;

        const alpis = [];
        for (let i = 0; i < alpiList.length; i++) {
            const currentAlpi = alpiList[i];
            alpis.push(
                <Alpi
                    key={i}
                    alpiGiven={currentAlpi}
                    shopContent={shopContent}
                    positionIndex={i}
                    filterTrackingValue={filterTrackingValue}
                    rankingTrackingValue={rankingTrackingValue}
                    pointOfSaleId={pointOfSaleId}
                    calculateLatestLoadedAlpi={calculateLatestLoadedAlpi}
                    latestAlpiIndexLoaded={latestAlpiIndexLoaded}
                    maxOrdering={maxOrdering}
                    navigateToProduct={navigateToProduct}
                    contentLoadedCounter={contentLoadedCounter}
                    teaserContent={teaserContent}
                />,
            );
        }
        yieldToMain(() => dispatch(loadOff()));
        return alpis;
    }

    function handleChunkLoading(newMaxIndex) {
        if (!loading.current) {
            if (shouldLoadNextChunk(newMaxIndex, chunkSize, currentChunk, numberOfEntryItems)) {
                const nextChunk = currentChunk.current + 1;
                loadChunkRange(nextChunk, nextChunk, articleCount);
                currentChunk.current = nextChunk;
                loading.current = true;
            }
        }
    }

    function shouldLoadNextChunk(newMaxIndex, chunkSize, currentChunk, numberOfEntryItems) {
        const currentLastAlpi = chunkSize * currentChunk.current + numberOfEntryItems.current;

        return (
            currentChunk.current < numberOfChunks &&
            currentLastAlpi - newMaxIndex <= Constants.CHUNK_LOADING_CUTOFF &&
            actualPageSize > currentLastAlpi
        );
    }

    function scrollToAnchorAfterLoad(alpiIndex) {
        const sliderHook = `[jsh-alpi-slider-anchor-${alpiIndex}]`;
        const slider = alpiListRef.current.querySelector(sliderHook);
        const alpi = slider.closest('[jsh-alpi]');

        window.requestAnimationFrame(() => {
            if (slider !== undefined && slider.offsetHeight > 0) {
                scrollToPagePosition(alpi.offsetTop);
            } else {
                scrollToAnchorAfterLoad(alpiIndex);
            }
        });
    }

    function scrollToPagePosition(scrollY) {
        window.scrollTo(0, scrollY);
        jumpToPreviousLocation.current = false;
    }

    function keepScrollPosition() {
        // only as soon as jumpToPreviousLocation is false, otherwise we overwrite the actual entry position
        if (!jumpToPreviousLocation.current) {
            // cannot take the middle exactly, because with 2 or 4 alpis (S,L,XL) there is always a gap
            const middleElement = document.elementFromPoint(
                window.innerWidth / 2 - Constants.ALPI_SPACE_BETWEEN,
                window.innerHeight / 2,
            );
            const alpi = middleElement.closest('[jsh-alpi]');
            if (alpi) {
                const alpiIndex = alpi.getAttribute('jsh-alpi');
                if (currentMiddleElement.current !== alpiIndex) {
                    currentMiddleElement.current = alpiIndex;
                    updateScrollPosition(alpiIndex);
                }
            }
        }
    }
}

export default React.memo(AlpiList);
